import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';

const IN_PROGRESS = new Set(['PENDING', 'DEPLOYING']);
const IDLE_POLL_MS = 30000;
const ACTIVE_POLL_MS = 10000;

export const isDeployInProgress = (deploy) => !!deploy && IN_PROGRESS.has(deploy.status);

/** Release bar data: main vs production and the latest deploy of each. Polls faster while deploying. */
export const useReleaseStatus = () => {
  const ensureToken = useAuthToken();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      await ensureToken();
      const response = await marketingSiteService.releaseStatus();
      setStatus(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load the release status.'));
      return null;
    }
  }, [ensureToken]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await refresh();
      if (cancelled) return;
      const active = data && (isDeployInProgress(data.testDeploy) || isDeployInProgress(data.prodDeploy));
      timer.current = setTimeout(tick, active ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [refresh]);

  return { status, error, refresh };
};
